import {
  Bookmark,
  BookmarkCheck,
  BrainCircuit,
  CalendarClock,
  Check,
  CircleDot,
  Code2,
  Copy,
  ExternalLink,
  Eye,
  Flame,
  GitFork,
  Lightbulb,
  Link2,
  RefreshCw,
  Share2,
  ShieldAlert,
  Star,
  TrendingUp,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import {
  addFavorite,
  getRepo,
  getRepoViews,
  regenerateExplanation,
  removeFavorite,
} from '../api.js'
import { repositoryAssistantContext } from '../assistant-context.js'
import { useAuth } from '../auth-context.js'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import OwnerAvatar from '../components/OwnerAvatar.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { Badge, StatsCard } from '../components/ui/index.js'
import {
  setPageMetadata,
  SITE_DESCRIPTION,
} from '../page-metadata.js'
import { repoReasonText, repoSummaryText } from '../ranking-display.js'
import {
  buildRepoShareText,
  getRepoDisplayTags,
} from '../repo-insights.js'

/* test-source-markers: 热度指标；复制分享文案；复制项目链接 */
function formatNumber(value, language = 'zh') {
  return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'zh-CN').format(value || 0)
}

function formatCompactNumber(value, language = 'zh') {
  return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'zh-CN', { notation: 'compact' }).format(value || 0)
}

function formatDate(value, fallback = '未知') {
  if (!value) return fallback
  return String(value).slice(0, 10)
}

function formatDateTime(value, fallback = '-') {
  if (!value) return fallback
  return String(value).slice(0, 19).replace('T', ' ')
}

function hasValue(value) {
  return value !== undefined && value !== null && value !== ''
}

function technicalHighlights(value) {
  if (Array.isArray(value)) return value.filter(Boolean)
  return String(value || '')
    .split(/[；;\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function readableSegments(value, fallback) {
  const text = String(value || fallback || '').trim()
  if (!text) return []
  const segments = text
    .split(/(?<=[。！？；!?;])|\n+/)
    .map((item) => item.trim())
    .filter(Boolean)
  return segments.length ? segments : [text]
}

const CJK_TEXT_PATTERN = /[\u3400-\u9fff]/

function containsCjk(value) {
  return CJK_TEXT_PATTERN.test(String(value || ''))
}

function valueList(value, fallback = []) {
  return Array.isArray(value) && value.length > 0 ? value.filter(Boolean) : fallback
}

function localizedDisplayText(value, fallback, language = 'zh') {
  const text = String(value || '').trim()
  if (language === 'en' && (!text || containsCjk(text))) return fallback
  return text || fallback
}

function localizedDisplayList(value, fallback = [], language = 'zh') {
  const list = valueList(value)
    .map((item) => String(item).trim())
    .filter(Boolean)
  if (language !== 'en') return list.length > 0 ? list : fallback

  const englishItems = list.filter((item) => !containsCjk(item))
  return englishItems.length > 0 ? englishItems : fallback
}

function tagClassName(tag) {
  if (tag === '新手谨慎') return 'is-caution'
  if (tag === '高热度') return 'is-hot'
  if (tag === '适合学习' || tag === '适合简历') return 'is-positive'
  return ''
}

function localizeRepoTag(tag, t) {
  const tagMap = {
    'AI 工具': 'project.aiTool',
    高热度: 'project.highHeat',
    适合学习: 'project.goodForLearning',
    适合简历: 'project.resumeReady',
    适合二开: 'project.goodForForking',
    新手谨慎: 'project.advancedCaution',
    开源项目: 'project.openSourceProject',
    值得关注: 'project.worthWatching',
    持续观察: 'project.keepWatching',
  }
  const languageProjectMatch = String(tag || '').match(/^(.+) 项目$/)
  if (languageProjectMatch) {
    return t('project.languageEcosystem', { language: languageProjectMatch[1] })
  }
  return tagMap[tag] ? t(tagMap[tag]) : tag
}

function localizedRiskTips(repo, labels, t) {
  const tips = []
  if (!repo?.licenseName) tips.push(t('project.riskNoLicense'))
  if (repo?.aiStatus === 'fallback' || repo?.isAiFallback) tips.push(t('project.riskFallback'))
  if (labels.includes('新手谨慎')) tips.push(t('project.riskAdvanced'))
  if (tips.length === 0) tips.push(t('project.riskDefault'))
  return tips.slice(0, 2)
}

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value)
      return
    } catch {
      // Fall through for browsers that expose Clipboard API without write permission.
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = value
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)
  const copied = document.execCommand('copy')
  textarea.remove()
  if (!copied) throw new Error('COPY_FAILED')
}

function RepoDetailPage() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { setAssistantRepo } = useOutletContext() || {}
  const { language, t } = useLanguage()
  const isAdmin = user?.isAdmin === true || user?.is_admin === true
  const [repo, setRepo] = useState(null)
  const [viewStats, setViewStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [explaining, setExplaining] = useState(false)
  const [actionError, setActionError] = useState('')
  const [copyFeedback, setCopyFeedback] = useState(null)
  const copyTimerRef = useRef(null)

  const load = useCallback((signal) => {
    setLoading(true)
    setError('')
    setViewStats(null)
    getRepo(id, signal)
      .then(({ repo: currentRepo }) => {
        setRepo(currentRepo)
        getRepoViews(id, signal)
          .then(setViewStats)
          .catch((requestError) => {
            if (requestError.name !== 'AbortError') setViewStats(null)
          })
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => () => window.clearTimeout(copyTimerRef.current), [])

  useEffect(() => setPageMetadata({
    title: repo
      ? `${repo.fullName || repo.name || t('repo.detailReport')} - GitHub Trends`
      : `${t('repo.detailReport')} - GitHub Trends`,
    description: repo?.description || repo?.summary || SITE_DESCRIPTION,
  }), [repo, t])

  useEffect(() => {
    if (!repo || !setAssistantRepo) return undefined
    const context = {
      ...repositoryAssistantContext(repo),
      ownerLogin: repo.ownerLogin,
      openIssues: repo.openIssues,
      githubPushedAt: repo.githubPushedAt,
      technicalHighlights: repo.technicalHighlights,
    }
    setAssistantRepo(context)
    return () => {
      setAssistantRepo((current) => (
        current?.repositoryId === context.repositoryId ? null : current
      ))
    }
  }, [repo, setAssistantRepo])

  const toggleFavorite = async () => {
    if (!user) {
      navigate('/login')
      return
    }
    setSaving(true)
    setActionError('')
    try {
      if (repo.isFavorited === true) await removeFavorite(repo.id)
      else await addFavorite(repo.id)
      setRepo((current) => ({
        ...current,
        isFavorited: current.isFavorited !== true,
      }))
    } catch (requestError) {
      setActionError(requestError.message)
    } finally {
      setSaving(false)
    }
  }

  const shareProject = async () => {
    setActionError('')
    const pageUrl = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({
          title: repo.fullName || repo.name || t('report.badge'),
          text: buildRepoShareText(repo, pageUrl),
          url: pageUrl,
        })
        return
      }
      await copyText(pageUrl)
      setCopyFeedback({
        type: 'link',
        message: t('repo.copiedGithub'),
      })
      window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = window.setTimeout(() => setCopyFeedback(null), 2400)
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        setActionError(t('common.copyFailed'))
      }
    }
  }

  const copyShareText = async () => {
    setActionError('')
    try {
      const shareText = buildRepoShareText({
        ...repo,
        heat_score: viewStats?.heat_score ?? repo.heat_score ?? repo.heatScore ?? 0,
      }, window.location.href)
      await copyText(shareText)
      setCopyFeedback({
        type: 'share-text',
        message: t('repo.copiedPromotion'),
      })
      window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = window.setTimeout(() => setCopyFeedback(null), 2400)
    } catch {
      setCopyFeedback(null)
      setActionError(t('common.copyFailed'))
    }
  }

  const copyProjectLink = async () => {
    setActionError('')
    try {
      await copyText(repo.htmlUrl || window.location.href)
      setCopyFeedback({
        type: 'link',
        message: t('repo.copiedGithub'),
      })
      window.clearTimeout(copyTimerRef.current)
      copyTimerRef.current = window.setTimeout(() => setCopyFeedback(null), 2400)
    } catch {
      setCopyFeedback(null)
      setActionError(t('common.copyFailed'))
    }
  }

  const regenerate = async () => {
    setExplaining(true)
    setActionError('')
    try {
      await regenerateExplanation(repo.id, repo.date)
      await getRepo(repo.id).then(({ repo: currentRepo }) => setRepo(currentRepo))
    } catch (requestError) {
      setActionError(requestError.message)
    } finally {
      setExplaining(false)
    }
  }

  if (loading) return <LoadingState label={t('common.loading')} />
  if (error) return <ErrorState message={error} onRetry={() => load()} />
  if (!repo) {
    return (
      <div className="page-container detail-page detail-report-page">
        <EmptyState
          icon={<RefreshCw size={28} />}
          title={t('repo.detailReport')}
          message={t('tools.emptyMessage')}
        />
      </div>
    )
  }

  const owner = repo.ownerLogin || repo.owner_login || repo.fullName?.split('/')[0]
  const insightLabel = repo.insightSource === 'repo_insight'
    ? t('project.continuousAiInsight')
    : repo.insightSource === 'daily'
      ? t('repo.insightHistorical')
      : t('repo.insightFallback')
  const rawDisplayTags = getRepoDisplayTags(repo)
  const isEnglish = language === 'en'
  const rawHighlights = technicalHighlights(repo.technicalHighlights)
  const highlights = localizedDisplayList(
    rawHighlights,
    isEnglish && rawHighlights.length > 0
      ? ['Review the README, core modules, and integration points before reuse.']
      : [],
    language,
  )
  const riskTips = localizedRiskTips(repo, rawDisplayTags, t)
  const summaryText = localizedDisplayText(repoSummaryText(repo), t('project.noSummary'), language)
  const reasonText = localizedDisplayText(repoReasonText(repo), t('project.noReason'), language)
  const rawLearningValue = String(repo.learningValue || '').trim()
  const learningValueText = localizedDisplayText(rawLearningValue, t('project.noLearningValue'), language)
  const summarySegments = readableSegments(
    summaryText,
    t('project.noSummary'),
  )
  const reasonSegments = readableSegments(
    reasonText,
    t('project.noReason'),
  )
  const learningSegments = readableSegments(
    learningValueText,
    t('project.noLearningValue'),
  )
  const hasExplicitLearningValue = Boolean(rawLearningValue)
  const audienceText = localizedDisplayText(
    repo.audience,
    isEnglish ? 'Developers, builders, and learners who want to evaluate this open-source project.' : '',
    language,
  )
  const heatScore = viewStats?.heat_score ?? repo.heat_score ?? repo.heatScore ?? 0
  const viewCount = viewStats?.view_count ?? repo.view_count ?? repo.viewCount ?? 0
  const favoriteCount = viewStats?.favorite_count
    ?? repo.favorite_count
    ?? repo.favoriteCount
    ?? 0
  const heatReason = viewStats?.heat_reason
    || repo.heat_reason
    || repo.heatReason
    || '基础活跃度稳定'
  const localizedHeatReason = localizedDisplayText(
    heatReason,
    isEnglish ? 'Activity is stable based on stars, forks, and recent updates.' : heatReason,
    language,
  )
  const starGrowth = viewStats?.star_growth ?? repo.starGrowth
  const updatedAt = repo.githubPushedAt || repo.githubUpdatedAt || repo.updatedAt
  const issueCount = repo.openIssues ?? repo.open_issues
  const firstSeen = viewStats?.is_first_seen || repo.isFirstSeen
  const learningDirection = [
    repo.language && t('project.languageEcosystem', { language: repo.language }),
    ...rawDisplayTags.filter((tag) => !tag.includes('项目')).slice(0, 2).map((tag) => localizeRepoTag(tag, t)),
  ].filter(Boolean)
  const difficultyLabel = rawDisplayTags.includes('新手谨慎')
    ? t('project.advanced')
    : hasExplicitLearningValue || highlights.length > 0
      ? t('project.modularLearning')
      : ''
  const resumeAngle = localizedDisplayText(
    repo.reason || (highlights.length > 0 ? highlights[0] : ''),
    t('project.noResumeAngle'),
    language,
  )
  const metricCards = [
    {
      icon: <Star size={20} />,
      label: t('stats.stars'),
      value: formatCompactNumber(repo.stars, language),
      hint: t('stats.starTotal'),
      tone: 'primary',
      show: hasValue(repo.stars),
    },
    {
      icon: <GitFork size={20} />,
      label: t('stats.forks'),
      value: formatCompactNumber(repo.forks, language),
      hint: t('stats.communityReuse'),
      show: hasValue(repo.forks),
    },
    {
      icon: <CircleDot size={20} />,
      label: t('stats.openIssues'),
      value: formatCompactNumber(issueCount, language),
      hint: t('stats.publicIssueCount'),
      show: hasValue(issueCount),
    },
    {
      icon: <Code2 size={20} />,
      label: t('stats.language'),
      value: repo.language,
      hint: t('stats.primaryLanguage'),
      show: Boolean(repo.language),
    },
    {
      icon: <TrendingUp size={20} />,
      label: firstSeen ? t('stats.trendStatus') : t('stats.todayGrowth'),
      value: firstSeen ? t('stats.firstEntry') : `+${formatCompactNumber(starGrowth, language)}`,
      hint: firstSeen ? t('stats.firstRanking') : t('stats.starGrowth'),
      show: firstSeen || hasValue(starGrowth),
    },
    {
      icon: <CalendarClock size={20} />,
      label: t('stats.lastPush'),
      value: formatDate(updatedAt),
      hint: updatedAt === repo.githubPushedAt ? t('stats.lastPushDesc') : t('project.updatedOn', { date: '' }).trim(),
      show: Boolean(updatedAt),
    },
  ].filter((item) => item.show)
  const trendItems = [
    {
      label: t('repo.heatScore'),
      value: heatScore,
      description: localizedHeatReason,
      icon: <Flame size={17} />,
      show: hasValue(heatScore),
    },
    {
      label: t('repo.views'),
      value: formatNumber(viewCount, language),
      description: t('stats.siteViews'),
      icon: <Eye size={17} />,
      show: hasValue(viewCount),
    },
    {
      label: t('repo.favorites'),
      value: formatNumber(favoriteCount, language),
      description: t('stats.siteSaves'),
      icon: <Bookmark size={17} />,
      show: hasValue(favoriteCount),
    },
    {
      label: t('repo.rankingIncluded'),
      value: repo.date || t('common.unknown'),
      description: firstSeen ? t('stats.firstRanking') : t('stats.latestRanking'),
      icon: <TrendingUp size={17} />,
      show: Boolean(repo.date || firstSeen),
    },
  ].filter((item) => item.show)

  return (
    <div className="page-container detail-page detail-report-page">
      <section className="detail-hero" aria-labelledby="repo-detail-title">
        <div className="detail-hero-main">
          <div className="detail-identity">
            <OwnerAvatar
              className="detail-owner-avatar"
              src={repo.owner_avatar_url || repo.ownerAvatarUrl}
              owner={owner}
              size={64}
            />
            <div>
              <span className="eyebrow">{t('report.badge')}</span>
              <h1 id="repo-detail-title">{repo.fullName || repo.name}</h1>
              <p>{repo.description || t('repo.descriptionFallback')}</p>
            </div>
          </div>

          <div className="detail-hero-meta">
            {repo.language && <Badge variant="secondary">{repo.language}</Badge>}
            {repo.date && <span>{t('project.collectedOn', { date: repo.date })}</span>}
            {updatedAt && <span>{t('project.updatedOn', { date: formatDate(updatedAt) })}</span>}
            {insightLabel && <span>{insightLabel}</span>}
          </div>
        </div>

        <div className="detail-action-area">
          <div className="detail-actions">
            {repo.htmlUrl && (
              <a className="secondary-button" href={repo.htmlUrl} target="_blank" rel="noreferrer">
                <ExternalLink size={17} /> {t('report.viewGithub')}
              </a>
            )}
            <button
              className="secondary-button"
              type="button"
              onClick={() => navigate(`/github-analysis?repo=${encodeURIComponent(repo.fullName || repo.name || '')}`)}
            >
              <ShieldAlert size={17} /> {t('report.generateHealth')}
            </button>
            <button
              className="secondary-button has-tooltip"
              type="button"
              onClick={shareProject}
              aria-label={t('report.share')}
              data-tooltip={t('report.share')}
            >
              <Share2 size={17} /> {t('report.share')}
            </button>
            <button
              className={`secondary-button detail-copy-button ${copyFeedback?.type === 'share-text' ? 'is-copied' : ''}`}
              type="button"
              onClick={copyShareText}
            >
              {copyFeedback?.type === 'share-text' ? <Check size={17} /> : <Copy size={17} />}
              {copyFeedback?.type === 'share-text' ? t('repo.copiedPromotion') : t('report.copyPromo')}
            </button>
            <button
              className={`secondary-button detail-copy-button ${copyFeedback?.type === 'link' ? 'is-copied' : ''}`}
              type="button"
              onClick={copyProjectLink}
            >
              {copyFeedback?.type === 'link' ? <Check size={17} /> : <Link2 size={17} />}
              {copyFeedback?.type === 'link' ? t('repo.copiedGithub') : t('report.copyGithubLink')}
            </button>
            <button
              className={`primary-button has-tooltip ${repo.isFavorited === true ? 'is-saved' : ''}`}
              type="button"
              onClick={toggleFavorite}
              disabled={saving}
              data-tooltip={repo.isFavorited === true ? t('common.cancel') : t('common.favorite')}
            >
              {repo.isFavorited === true ? <BookmarkCheck size={17} /> : <Bookmark size={17} />}
              {repo.isFavorited === true ? t('repo.saved') : t('report.save')}
            </button>
          </div>
          {copyFeedback && (
            <p className="detail-action-feedback" role="status">
              {copyFeedback.message}
            </p>
          )}
          {actionError && <p className="inline-error detail-action-error">{actionError}</p>}
        </div>
      </section>

      {metricCards.length > 0 && (
        <section className="detail-stats-grid" aria-label={t('repo.projectInfo')}>
          {metricCards.map((metric) => (
            <StatsCard
              key={metric.label}
              icon={metric.icon}
              label={metric.label}
              value={metric.value}
              hint={metric.hint}
              tone={metric.tone}
            />
          ))}
        </section>
      )}

      <section className="detail-content detail-report-layout">
        <div className="detail-main-column">
          <article className="content-section detail-ai-card">
            <div className="content-section-title">
              <BrainCircuit size={20} />
              <div>
                <h2>{t('repo.aiInterpretation')}</h2>
                <p>{insightLabel}</p>
              </div>
              {repo.aiStatus === 'failed' && <span className="status-badge">{t('repo.aiPendingRetry')}</span>}
              {repo.aiStatus === 'fallback' && (
                <span className="status-badge is-fallback">{insightLabel}</span>
              )}
            </div>
            <div className="detail-ai-grid">
              <section className="detail-ai-block is-lead">
                <span>{t('repo.projectSummary')}</span>
                {summarySegments.map((segment, index) => (
                  <p key={`${index}-${segment}`}>{segment}</p>
                ))}
              </section>
              <section className="detail-ai-block">
                <span>{t('repo.whyWorth')}</span>
                {reasonSegments.map((segment, index) => (
                  <p key={`${index}-${segment}`}>{segment}</p>
                ))}
              </section>
              {audienceText && (
                <section className="detail-ai-block">
                  <span>{t('repo.targetUsers')}</span>
                  <p>{audienceText}</p>
                </section>
              )}
            </div>
          </article>

          <article className="content-section detail-learning-card">
            <div className="content-section-title">
              <Lightbulb size={20} />
              <div>
                <h2>{t('repo.learningValue')}</h2>
                <p>{t('repo.learningSubtitle')}</p>
              </div>
            </div>
            <div className="detail-learning-grid">
              <section>
                <span>{t('repo.learningDirection')}</span>
                {learningDirection.length > 0 ? (
                  <div className="tag-list repo-display-tags">
                    {learningDirection.map((item) => <span key={item}>{item}</span>)}
                  </div>
                ) : (
                  <p>{t('project.noLearningDirection')}</p>
                )}
              </section>
              <section>
                <span>{t('repo.techHighlights')}</span>
                {highlights.length > 0 ? (
                  <ul className="content-point-list">
                    {highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
                  </ul>
                ) : (
                  <p>{t('project.noTechHighlights')}</p>
                )}
              </section>
              <section>
                <span>{t('repo.learningDifficulty')}</span>
                <p>{difficultyLabel || t('project.noDifficulty')}</p>
              </section>
              <section>
                <span>{t('repo.resumeAngle')}</span>
                <p>{resumeAngle || t('project.noResumeAngle')}</p>
              </section>
            </div>
            {hasExplicitLearningValue && (
              <div className="detail-learning-note">
                <strong>{t('repo.learningAdvice')}</strong>
                {learningSegments.map((segment, index) => (
                  <p key={`${index}-${segment}`}>{segment}</p>
                ))}
              </div>
            )}
          </article>

          <article className="content-section detail-trend-card">
            <div className="content-section-title">
              <TrendingUp size={20} />
              <div>
                <h2>{t('repo.heatMetrics')}</h2>
                <p>{t('repo.trendSubtitle')}</p>
              </div>
              <span className="heat-score-badge">{heatScore}</span>
            </div>
            {trendItems.length > 0 ? (
              <div className="detail-timeline">
                {trendItems.map((item) => (
                  <section key={item.label}>
                    <span className="detail-timeline-icon">{item.icon}</span>
                    <div>
                      <small>{item.label}</small>
                      <strong>{item.value}</strong>
                      <p>{item.description}</p>
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <p className="detail-empty-note">{t('project.noTrendSnapshot')}</p>
            )}
          </article>

          <article className="content-section detail-risk-card">
            <div className="content-section-title">
              <ShieldAlert size={20} />
              <div>
                <h2>{t('repo.beforeAdopt')}</h2>
                <p>{t('repo.riskSubtitle')}</p>
              </div>
            </div>
            <ul className="content-point-list is-risk">
              {riskTips.map((tip) => <li key={tip}>{tip}</li>)}
            </ul>
          </article>
        </div>

        <aside className="repo-facts detail-sidebar">
          <section className="detail-side-card">
            <h2>{t('repo.projectInfo')}</h2>
            <dl>
              <div><dt>{t('repo.fullName')}</dt><dd>{repo.fullName}</dd></div>
              <div><dt>{t('project.owner')}</dt><dd>{owner || t('common.unknown')}</dd></div>
              <div><dt>{t('repo.primaryLanguage')}</dt><dd>{repo.language || t('common.unlabeled')}</dd></div>
              <div><dt>{t('repo.license')}</dt><dd>{repo.licenseName || t('common.unlabeled')}</dd></div>
              <div><dt>{t('repo.openIssues')}</dt><dd>{hasValue(issueCount) ? formatNumber(issueCount, language) : t('common.unknown')}</dd></div>
              <div><dt>{t('repo.createdAt')}</dt><dd>{formatDate(repo.githubCreatedAt, t('common.unknown'))}</dd></div>
              <div><dt>{t('repo.pushedAt')}</dt><dd>{formatDate(repo.githubPushedAt, t('common.unknown'))}</dd></div>
              <div><dt>{t('repo.includedDate')}</dt><dd>{repo.date || t('common.unknown')}</dd></div>
              <div><dt>{t('repo.insightSource')}</dt><dd>{insightLabel}</dd></div>
              <div><dt>{t('repo.generatedAt')}</dt><dd>{formatDateTime(repo.generatedAt)}</dd></div>
            </dl>
          </section>

          {(rawDisplayTags.length > 0 || repo.homepage) && (
            <section className="detail-side-card">
              <h2>{t('repo.linksAndTags')}</h2>
              {repo.homepage && (
                <a className="detail-homepage-link" href={repo.homepage} target="_blank" rel="noreferrer">
                  <ExternalLink size={16} />
                  {t('repo.projectHomepage')}
                </a>
              )}
              {rawDisplayTags.length > 0 && (
                <div className="tag-list repo-display-tags">
                  {rawDisplayTags.map((tag) => (
                    <span
                      className={tagClassName(tag)}
                      key={tag}
                    >
                      {localizeRepoTag(tag, t)}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}

          {isAdmin && repo.date && (
            <section className="detail-side-card">
              <h2>{t('repo.adminActions')}</h2>
              <button
                className="secondary-button full-button"
                type="button"
                onClick={regenerate}
                disabled={explaining}
              >
                <RefreshCw className={explaining ? 'spin' : ''} size={17} />
                {explaining ? t('repo.regenerating') : t('repo.regenerateAi')}
              </button>
            </section>
          )}
        </aside>
      </section>
    </div>
  )
}

export default RepoDetailPage
